/**
 * GA4 marketing analytics — event names must match the Python Marketing Analytics pipeline.
 *
 * GA4 Admin → Custom definitions → Create custom dimension:
 *   - Dimension name: Calculator name
 *   - Scope: Event
 *   - Event parameter: calculator_name
 */

import { analyticsMode, hasAnalyticsConsent } from './consent';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

const FIRST_TOUCH_KEY = 'bpa_first_touch_utm';
const LATEST_TOUCH_KEY = 'bpa_latest_touch_utm';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

const UTM_SESSION_PREFIX = 'bpa_session_utm_';
const UTM_SESSION_KEYS = UTM_KEYS;

export const CALCULATOR_EVENT_NAMES = Object.freeze({
  OPENED: 'calculator_opened',
  STARTED: 'calculator_start',
  RESULT_VIEWED: 'calculator_result_view',
  COMPLETED: 'calculator_completed',
});

export const CALCULATOR_NAMES = Object.freeze({
  PAYCHECK_TAX: 'tax_projection',
  RETIREMENT_GAP: 'retirement_projection',
  SOCIAL_SECURITY: 'social_security_projection',
  ROTH_TRADITIONAL: 'roth_analysis',
  HOUSING_DECISION: 'housing_decision',
  INVESTMENT_PERFORMANCE: 'investment_performance',
  CAR_LEASE_BUY: 'car_lease_buy',
});

/** Maps in-app calculator IDs to marketing pipeline calculator_name values. */
export const CALCULATOR_ANALYTICS_IDS = {
  paycheck_tax_estimator: CALCULATOR_NAMES.PAYCHECK_TAX,
  retirement_gap_estimator: CALCULATOR_NAMES.RETIREMENT_GAP,
  social_security_estimator: CALCULATOR_NAMES.SOCIAL_SECURITY,
  roth_vs_traditional_calculator: CALCULATOR_NAMES.ROTH_TRADITIONAL,
  housing_decision_calculator: CALCULATOR_NAMES.HOUSING_DECISION,
  investment_performance_explorer: CALCULATOR_NAMES.INVESTMENT_PERFORMANCE,
  car_lease_buy_calculator: CALCULATOR_NAMES.CAR_LEASE_BUY,
};

const ALLOWED_EVENT_PARAMS = new Set([
  'button_id',
  'button_label',
  'destination_type',
  'destination_url_domain',
  'calculator_id',
  'calculator_name',
  'source_calculator',
  'calculator_step',
  'field_name',
  'result_category',
  'income_type',
  'has_self_employment_income',
  'filing_status',
  'result_type',
  'traffic_source',
  'traffic_medium',
  'traffic_campaign',
  'form_name',
  'signup_method',
  'onboarding_step',
  'page_path',
  'booking_type',
  'source_page',
  'link_url',
  'link_text',
  // Services & Pricing attribution (no PII / no form contents)
  'selected_offer',
  'pricing_model',
  'content_category',
  'promotional_angle',
  'offer_id',
  'faq_id',
  'faq_action',
  'guide_choice',
  'interaction_type',
  'cta_location',
  // Funnel events: offer and amount only (never who paid or signed)
  'engagement_kind',
  'value',
  'currency',
  'original_landing_page',
  'referring_page',
  ...UTM_KEYS,
]);

/**
 * Public marketing routes that may send GA4 page_view / funnel events.
 * Authenticated client-report and portal surfaces are excluded.
 */
export const PUBLIC_MARKETING_PATHS = Object.freeze([
  '/',
  '/links',
  '/linktree',
  '/financial-planning-services',
  '/services-and-pricing',
  '/privacy-policy',
  '/privacy-policy-financial-planning',
  '/calculators',
  '/housing-decision-calculator',
  '/rent-vs-buy-housing-calculator',
  '/paycheck-tax-estimator',
  '/retirement-gap-estimator',
  '/roth-vs-traditional-calculator',
  '/social-security-estimator',
  '/investment-performance-explorer',
  '/thank-you',
  '/booking-confirmed',
  '/book',
  '/goal-setting-meeting',
  '/agreement-signed',
]);

/**
 * Signing links carry a secret token in the path, and /send is the advisor's
 * document tool. Analytics is never loaded or sent while one of these is the
 * current URL.
 */
export function isTokenPath(pathname = '') {
  return /^\/(agreement|planner|sign|send)(\/|$)/.test(String(pathname || ''));
}

/** True for public marketing paths; false for portal/login/client-report App routes. */
export function isPublicMarketingPath(pathname = '') {
  const path = String(pathname || '').split('?')[0] || '/';
  if (path === '/') return true;
  return PUBLIC_MARKETING_PATHS.some(
    (prefix) => prefix !== '/' && (path === prefix || path.startsWith(`${prefix}/`))
  );
}

export const PRICING_EVENT_NAMES = Object.freeze({
  NAV_CLICK: 'services_nav_click',
  PAGE_VIEW: 'pricing_page_view',
  SERVICE_CARD_VIEW: 'service_card_view',
  SERVICE_CARD_INTERACT: 'service_card_interact',
  OFFER_DETAIL_CLICK: 'offer_detail_click',
  CTA_CLICK: 'pricing_cta_click',
  FAQ_INTERACT: 'pricing_faq_interact',
  GUIDE_SELECT: 'service_guide_select',
});

/**
 * service_card_view fires once per offer per session only after the card is at least
 * 50% visible continuously for 1 second (meaningful impression, not a scroll flash).
 */
export const SERVICE_CARD_VIEW_THRESHOLD = 0.5;
export const SERVICE_CARD_VIEW_DWELL_MS = 1000;

const ORIGINAL_LANDING_KEY = 'bpa_original_landing_page';
const REFERRING_PAGE_KEY = 'bpa_referring_page';
const SELECTED_OFFER_KEY = 'bpa_selected_offer';

/**
 * localStorage identifiers (first/latest touch, first-visit flags) persist
 * across visits, so they need analytics consent. sessionStorage attribution ends with the
 * tab and stays on the site, so lead-form attribution keeps working without it.
 */
function canUseBrowserStorage(storage = window.localStorage) {
  try {
    if (typeof window === 'undefined' || typeof storage === 'undefined') return false;
    if (storage === window.localStorage && !hasAnalyticsConsent()) return false;
    return true;
  } catch (_error) {
    return false;
  }
}

function readStoredJson(key, storage = window.localStorage) {
  if (!canUseBrowserStorage(storage)) return {};
  try {
    return JSON.parse(storage.getItem(key) || '{}') || {};
  } catch (_error) {
    return {};
  }
}

function writeStoredJson(key, value, storage = window.localStorage) {
  if (!canUseBrowserStorage(storage)) return;
  storage.setItem(key, JSON.stringify(value));
}

export function readUtmParams(search = window.location.search) {
  const params = new URLSearchParams(search);
  return UTM_KEYS.reduce((acc, key) => {
    const value = params.get(key);
    if (value) acc[key] = value;
    return acc;
  }, {});
}

/**
 * Persist all standard UTM params from the landing URL into sessionStorage
 * for attachment to conversion events. Call once on app mount and on route search changes.
 */
export function initUtmCapture(search = window.location.search) {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(search);
  UTM_SESSION_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value && canUseBrowserStorage(window.sessionStorage)) {
      window.sessionStorage.setItem(`${UTM_SESSION_PREFIX}${key}`, value);
    }
  });
}

export function getSessionUtmParams() {
  if (typeof window === 'undefined' || !canUseBrowserStorage(window.sessionStorage)) {
    return {};
  }

  return UTM_SESSION_KEYS.reduce((acc, key) => {
    const value = window.sessionStorage.getItem(`${UTM_SESSION_PREFIX}${key}`);
    if (value) acc[key] = value;
    return acc;
  }, {});
}

export function preserveUtmParams(search = window.location.search) {
  initUtmCapture(search);

  const latestTouch = readUtmParams(search);
  if (Object.keys(latestTouch).length === 0) return;

  const payload = {
    ...latestTouch,
    captured_at: new Date().toISOString(),
  };

  if (Object.keys(readStoredJson(FIRST_TOUCH_KEY)).length === 0) {
    writeStoredJson(FIRST_TOUCH_KEY, payload);
  }

  writeStoredJson(LATEST_TOUCH_KEY, payload);
}

function pickUtmFromTouch(touch = {}) {
  return UTM_KEYS.reduce((acc, key) => {
    if (touch[key]) acc[key] = touch[key];
    return acc;
  }, {});
}

export function getStoredUtmParams() {
  const sessionUtm = getSessionUtmParams();
  const latestTouch = readStoredJson(LATEST_TOUCH_KEY);
  const firstTouch = readStoredJson(FIRST_TOUCH_KEY);
  const localTouch = Object.keys(latestTouch).length > 0 ? latestTouch : firstTouch;
  const localUtm = pickUtmFromTouch(localTouch);

  return UTM_KEYS.reduce((acc, key) => {
    const value = sessionUtm[key] || localUtm[key];
    if (value) acc[key] = value;
    return acc;
  }, {});
}

/**
 * Copy stored / current-page UTMs onto an internal or https URL without
 * overwriting params the destination already has. Leaves mailto/sms/tel/# alone.
 */
export function withStoredUtms(url, search = typeof window !== 'undefined' ? window.location.search : '') {
  const raw = String(url || '');
  if (!raw || /^(mailto:|sms:|tel:|#)/i.test(raw)) return raw;

  const incoming = {
    ...getStoredUtmParams(),
    ...readUtmParams(search),
  };
  if (!Object.keys(incoming).length) return raw;

  try {
    const origin = typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://example.invalid';
    const absolute = /^https?:\/\//i.test(raw);
    const next = new URL(raw, origin);
    UTM_KEYS.forEach((key) => {
      if (incoming[key] && !next.searchParams.get(key)) {
        next.searchParams.set(key, incoming[key]);
      }
    });
    if (absolute) return next.toString();
    return `${next.pathname}${next.search}${next.hash}`;
  } catch (_error) {
    return raw;
  }
}

export function rememberSelectedOffer(offerId) {
  const value = String(offerId || '').trim();
  if (!value || typeof window === 'undefined' || !canUseBrowserStorage(window.sessionStorage)) return;
  window.sessionStorage.setItem(SELECTED_OFFER_KEY, value.slice(0, 100));
}

export function getRememberedOffer() {
  if (typeof window === 'undefined' || !canUseBrowserStorage(window.sessionStorage)) return '';
  return window.sessionStorage.getItem(SELECTED_OFFER_KEY) || '';
}

/** Capture first landing path (+ optional hash) for the session. Safe path only — no PII. */
export function captureOriginalLandingPage(pathname = getPagePath(), hash = '') {
  if (typeof window === 'undefined' || !canUseBrowserStorage(window.sessionStorage)) return;
  if (window.sessionStorage.getItem(ORIGINAL_LANDING_KEY)) return;
  const cleanHash = String(hash || window.location.hash || '').replace(/^#/, '');
  const value = `${pathname || '/'}${cleanHash ? `#${cleanHash}` : ''}`.slice(0, 100);
  window.sessionStorage.setItem(ORIGINAL_LANDING_KEY, value);
}

export function getOriginalLandingPage() {
  if (typeof window === 'undefined' || !canUseBrowserStorage(window.sessionStorage)) return '';
  return window.sessionStorage.getItem(ORIGINAL_LANDING_KEY) || '';
}

/** Last in-app path before the current page (updated on each page_view). */
export function captureReferringPage(previousPath) {
  if (typeof window === 'undefined' || !canUseBrowserStorage(window.sessionStorage)) return;
  if (!previousPath) return;
  window.sessionStorage.setItem(REFERRING_PAGE_KEY, String(previousPath).slice(0, 100));
}

export function getReferringPage() {
  if (typeof window === 'undefined' || !canUseBrowserStorage(window.sessionStorage)) return '';
  return window.sessionStorage.getItem(REFERRING_PAGE_KEY) || '';
}

function getCampaignEventParams() {
  const utm = getStoredUtmParams();
  const originalLanding = getOriginalLandingPage();
  const referringPage = getReferringPage();
  return {
    ...utm,
    ...(utm.utm_source ? { traffic_source: utm.utm_source } : {}),
    ...(utm.utm_medium ? { traffic_medium: utm.utm_medium } : {}),
    ...(utm.utm_campaign ? { traffic_campaign: utm.utm_campaign } : {}),
    ...(originalLanding ? { original_landing_page: originalLanding } : {}),
    ...(referringPage ? { referring_page: referringPage } : {}),
  };
}

function sanitizeParams(params = {}) {
  const merged = {
    ...getCampaignEventParams(),
    ...params,
  };

  return Object.entries(merged).reduce((acc, [key, value]) => {
    if (!ALLOWED_EVENT_PARAMS.has(key)) return acc;
    if (value == null || value === '') return acc;
    acc[key] = String(value).slice(0, 100);
    return acc;
  }, {});
}

function debugLog(eventName, params) {
  if (process.env.NODE_ENV !== 'development') return;
  if (typeof window === 'undefined') return;

  const entry = { event: eventName, params, at: new Date().toISOString() };
  window.__analyticsDebug = window.__analyticsDebug || [];
  window.__analyticsDebug.push(entry);
  // eslint-disable-next-line no-console
  console.debug('[analytics]', eventName, params);
}

function sendEvent(eventName, params = {}) {
  debugLog(eventName, params);

  if (!GA_MEASUREMENT_ID || typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  if (analyticsMode() === 'off') return;

  // Do not send marketing events from authenticated client-report surfaces.
  if (!isPublicMarketingPath(getPagePath())) {
    return;
  }

  window.gtag('event', eventName, sanitizeParams(params));
}

function getPagePath() {
  if (typeof window === 'undefined') return '';
  return window.location.pathname || '';
}

function getSourcePage() {
  if (typeof window === 'undefined') return '';
  return document.referrer || window.location.pathname || '';
}

function sessionFlagKey(name) {
  return `bpa_analytics_${name}`;
}

function hasSessionFlag(name) {
  if (!canUseBrowserStorage(window.sessionStorage)) return false;
  return window.sessionStorage.getItem(sessionFlagKey(name)) === '1';
}

function setSessionFlag(name) {
  if (!canUseBrowserStorage(window.sessionStorage)) return;
  window.sessionStorage.setItem(sessionFlagKey(name), '1');
}

function trackCalculatorEvent(eventName, { calculatorName, calculatorId, ...params } = {}, { dedupePerSession = false } = {}) {
  if (!calculatorName) return;
  const eventParams = {
    calculator_name: calculatorName,
    ...(calculatorId ? { calculator_id: calculatorId } : {}),
    ...params,
  };

  if (!dedupePerSession) {
    sendEvent(eventName, eventParams);
    return;
  }

  const dedupeKey = `${eventName}_${calculatorId || calculatorName}`;
  if (hasSessionFlag(dedupeKey)) return;
  setSessionFlag(dedupeKey);
  sendEvent(eventName, eventParams);
}

export function trackCalculatorOpened(params) { trackCalculatorEvent(CALCULATOR_EVENT_NAMES.OPENED, params, { dedupePerSession: true }); }
export function trackCalculatorStarted(params) { trackCalculatorEvent(CALCULATOR_EVENT_NAMES.STARTED, params); }
export function trackCalculatorResultViewed(params) { trackCalculatorEvent(CALCULATOR_EVENT_NAMES.RESULT_VIEWED, params, { dedupePerSession: true }); }
export function trackCalculatorCompleted(params) { trackCalculatorEvent(CALCULATOR_EVENT_NAMES.COMPLETED, params, { dedupePerSession: true }); }

export function trackCalculatorFaqView(params = {}) {
  const key = `calculator_faq_view_${params.calculator_id || params.calculator_name || 'calculator'}_${params.cta_location || 'faq'}`;
  if (hasSessionFlag(key)) return;
  setSessionFlag(key);
  sendEvent('calculator_faq_view', params);
}

export function trackCalculatorFaqExpand(params = {}) {
  sendEvent('calculator_faq_expand', { interaction_type: 'expand', ...params });
}

export function trackCalculatorRelatedLinkClick(params = {}) {
  sendEvent('calculator_related_link_click', params);
}

/**
 * Discovery / contact form engagement start.
 * Not a completed lead — fires on first meaningful field interaction.
 */
export function trackContactFormStart(formName, params = {}) {
  const dedupeKey = `contact_start_${formName}_${getPagePath()}`;
  if (hasSessionFlag(dedupeKey)) return;
  setSessionFlag(dedupeKey);

  sendEvent('contact_form_start', {
    form_name: formName,
    page_path: getPagePath(),
    cta_location: params.cta_location || 'intake_form',
    ...params,
  });
}

/**
 * Successful discovery form submission (lead created).
 * Distinct from pricing_cta_click / link_click — those are not completed leads.
 */
export function trackContactFormSubmit(formName, params = {}) {
  if (params.offer_id || params.selected_offer) {
    rememberSelectedOffer(params.offer_id || params.selected_offer);
  }
  sendEvent('contact_form_submit', {
    form_name: formName,
    page_path: getPagePath(),
    cta_location: params.cta_location || 'intake_form',
    ...params,
  });
}

function analyticsSafeLinkUrl(linkUrl) {
  const value = String(linkUrl || '');
  if (value.startsWith('mailto:')) return 'mailto:';
  if (value.startsWith('sms:')) return 'sms:';
  if (value.startsWith('tel:')) return 'tel:';
  return value.slice(0, 100);
}

/**
 * Confirmed booking — only when TidyCal or in-app /book returns with booked=1
 * on /thank-you or /booking-confirmed. Do not fire from CTA clicks alone.
 */
export function trackDiscoveryCallBooked(bookingType = 'tidycal_discovery_call', params = {}) {
  sendEvent('discovery_call_booked', {
    booking_type: bookingType,
    source_page: getSourcePage(),
    page_path: getPagePath(),
    cta_location: params.cta_location || 'booking_confirmation',
    ...params,
  });
}

/**
 * Booking handoff initiated (TidyCal exit or in-app /book entry).
 * Not a confirmed booking — use discovery_call_booked only on booked=1 return.
 */
export function trackDiscoveryCallStarted(linkUrl, linkText, params = {}) {
  sendEvent('discovery_call_started', {
    link_url: analyticsSafeLinkUrl(linkUrl),
    link_text: String(linkText || '').slice(0, 100),
    destination_type: 'booking',
    booking_type: params.booking_type || 'tidycal_discovery_call',
    source_page: getSourcePage(),
    page_path: getPagePath(),
    cta_location: params.cta_location || 'booking_handoff',
    ...params,
  });
}

export function trackFinancialCheckupClicked(params = {}) {
  sendEvent('financial_checkup_clicked', {
    calculator_id: 'paycheck_tax_estimator',
    calculator_name: CALCULATOR_NAMES.PAYCHECK_TAX,
    page_path: getPagePath(),
    ...params,
  });
}

export function trackCalculatorFinancialCheckupCtaClick(params = {}) {
  sendEvent('calculator_financial_checkup_cta_click', {
    page_path: getPagePath(),
    ...params,
  });
}

export function trackCalculatorSendResultCtaClick(params = {}) {
  sendEvent('calculator_send_result_cta_click', {
    page_path: getPagePath(),
    ...params,
  });
}

export function trackLinkClick(linkUrl, linkText, params = {}) {
  sendEvent('link_click', {
    link_url: analyticsSafeLinkUrl(linkUrl),
    link_text: String(linkText || '').slice(0, 100),
    ...params,
  });
}

function withPricingContext(params = {}) {
  return {
    page_path: getPagePath(),
    source_page: getSourcePage(),
    referring_page: getReferringPage() || undefined,
    original_landing_page: getOriginalLandingPage() || undefined,
    ...params,
  };
}

/** Primary / mobile nav click to Services & Pricing (also emits link_click). */
export function trackServicesNavClick(linkUrl, linkText, params = {}) {
  const payload = withPricingContext({
    link_url: analyticsSafeLinkUrl(linkUrl),
    link_text: String(linkText || '').slice(0, 100),
    destination_type: 'internal',
    button_id: params.button_id || 'services_pricing_nav',
    button_label: params.button_label || linkText,
    ...params,
  });
  trackLinkClick(linkUrl, linkText, payload);
  sendEvent(PRICING_EVENT_NAMES.NAV_CLICK, payload);
}

/** Dedicated pricing page view (deduped per session; page_view still fires separately). */
export function trackPricingPageView(params = {}) {
  const dedupeKey = `pricing_page_view_${getPagePath()}`;
  if (hasSessionFlag(dedupeKey)) return;
  setSessionFlag(dedupeKey);
  sendEvent(PRICING_EVENT_NAMES.PAGE_VIEW, withPricingContext(params));
}

/**
 * Fire a deduped service_card_view (once per offer per session).
 * Prefer observeServiceCardVisibility() so the event only fires after
 * ≥50% visibility for 1 continuous second.
 */
export function trackServiceCardView(params = {}) {
  const offerKey = params.offer_id || params.selected_offer || 'unknown';
  const dedupeKey = `service_card_view_${offerKey}`;
  if (hasSessionFlag(dedupeKey)) return false;
  setSessionFlag(dedupeKey);
  sendEvent(PRICING_EVENT_NAMES.SERVICE_CARD_VIEW, withPricingContext(params));
  return true;
}

/**
 * Observe an offer card and fire service_card_view after a meaningful impression:
 * intersection ratio ≥ SERVICE_CARD_VIEW_THRESHOLD (0.5) for SERVICE_CARD_VIEW_DWELL_MS (1000ms).
 * Deduped per offer per session. Returns a cleanup function.
 */
export function observeServiceCardVisibility(element, params = {}, options = {}) {
  const threshold = options.threshold ?? SERVICE_CARD_VIEW_THRESHOLD;
  const dwellMs = options.dwellMs ?? SERVICE_CARD_VIEW_DWELL_MS;
  const offerKey = params.offer_id || params.selected_offer || 'unknown';
  const dedupeKey = `service_card_view_${offerKey}`;

  if (!element || hasSessionFlag(dedupeKey)) {
    return () => {};
  }

  if (typeof IntersectionObserver === 'undefined') {
    return () => {};
  }

  let dwellTimer = null;
  let cleaned = false;

  const clearDwell = () => {
    if (dwellTimer) {
      clearTimeout(dwellTimer);
      dwellTimer = null;
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      if (cleaned || hasSessionFlag(dedupeKey)) {
        clearDwell();
        observer.disconnect();
        return;
      }

      const entry = entries[0];
      const visibleEnough = entry && entry.isIntersecting && entry.intersectionRatio >= threshold;

      if (visibleEnough) {
        if (!dwellTimer) {
          dwellTimer = setTimeout(() => {
            dwellTimer = null;
            if (cleaned || hasSessionFlag(dedupeKey)) return;
            trackServiceCardView(params);
            observer.disconnect();
          }, dwellMs);
        }
      } else {
        clearDwell();
      }
    },
    { threshold: [0, threshold, 1] }
  );

  observer.observe(element);

  return () => {
    cleaned = true;
    clearDwell();
    observer.disconnect();
  };
}

export function trackServiceCardInteract(params = {}) {
  sendEvent(PRICING_EVENT_NAMES.SERVICE_CARD_INTERACT, withPricingContext({
    interaction_type: params.interaction_type || 'click',
    ...params,
  }));
}

export function trackOfferDetailClick(params = {}) {
  sendEvent(PRICING_EVENT_NAMES.OFFER_DETAIL_CLICK, withPricingContext(params));
}

/**
 * Pricing / offer CTA. Also emits link_click so existing pipeline metrics stay intact.
 * Prefer this over a bare trackLinkClick for offer CTAs.
 */
/**
 * Pricing / offer CTA click — interest signal only.
 * Does NOT mean a lead was submitted or a meeting was booked.
 */
export function trackPricingCtaClick(linkUrl, linkText, params = {}) {
  const payload = withPricingContext({
    link_url: analyticsSafeLinkUrl(linkUrl),
    link_text: String(linkText || '').slice(0, 100),
    destination_type: params.destination_type || 'contact_form',
    button_id: params.button_id,
    button_label: params.button_label || linkText,
    selected_offer: params.selected_offer || params.offer_id,
    offer_id: params.offer_id || params.selected_offer,
    pricing_model: params.pricing_model,
    content_category: params.content_category,
    promotional_angle: params.promotional_angle,
    cta_location: params.cta_location || 'pricing_page',
    ...params,
  });
  trackLinkClick(linkUrl, linkText, payload);
  sendEvent(PRICING_EVENT_NAMES.CTA_CLICK, payload);
}

export function trackPricingFaqInteract(params = {}) {
  sendEvent(PRICING_EVENT_NAMES.FAQ_INTERACT, withPricingContext({
    faq_action: params.faq_action || 'toggle',
    ...params,
  }));
}

export function trackServiceGuideSelect(params = {}) {
  sendEvent(PRICING_EVENT_NAMES.GUIDE_SELECT, withPricingContext(params));
}

let lastBookedDiscoveryLocation = null;

/**
 * Tracks a booking confirmation once for the current SPA page load.
 * Requires booked=1 on /thank-you or /booking-confirmed (TidyCal redirect or in-app /book).
 * Without that return signal, confirmed bookings are not counted.
 */
export function trackBookedDiscoveryConfirmation({ pathname, search } = {}) {
  const params = new URLSearchParams(search || '');
  if (params.get('booked') !== '1') return false;

  const locationKey = `${pathname || ''}?${params.toString()}`;
  if (lastBookedDiscoveryLocation === locationKey) return false;
  lastBookedDiscoveryLocation = locationKey;
  const bookingType = params.get('booking_type') || 'tidycal_discovery_call';
  const offerId = params.get('offer') || getRememberedOffer() || '';
  trackDiscoveryCallBooked(bookingType, {
    cta_location: 'booking_confirmation',
    offer_id: offerId || undefined,
    selected_offer: offerId || undefined,
  });
  return true;
}

/**
 * Funnel stages, each a distinct event:
 * landing-page visit → booking start → checkout start → paid booking → agreement signed → plan purchase.
 * Params are offer, amount and campaign labels only.
 */
export const FUNNEL_EVENT_NAMES = Object.freeze({
  MEETING_PAGE_VIEW: 'meeting_page_view',
  BOOKING_STARTED: 'booking_started',
  CHECKOUT_STARTED: 'begin_checkout',
  MEETING_BOOKED: 'goal_setting_meeting_booked',
  AGREEMENT_SIGNED: 'agreement_signed',
  PLAN_PURCHASE: 'plan_purchase',
});

function funnelParams(params = {}) {
  return {
    source_page: getSourcePage(),
    page_path: getPagePath(),
    ...params,
  };
}

function oncePerSession(key) {
  if (typeof window === 'undefined' || !canUseBrowserStorage(window.sessionStorage)) return true;
  const storageKey = `bpa_funnel_${key}`;
  if (window.sessionStorage.getItem(storageKey)) return false;
  window.sessionStorage.setItem(storageKey, '1');
  return true;
}

/** Goal-Setting Meeting details page viewed. */
export function trackMeetingPageView(params = {}) {
  sendEvent(FUNNEL_EVENT_NAMES.MEETING_PAGE_VIEW, funnelParams(params));
}

/** A meeting time was chosen on the booking page (once per session). */
export function trackBookingStarted(params = {}) {
  if (!oncePerSession('booking_started')) return;
  sendEvent(FUNNEL_EVENT_NAMES.BOOKING_STARTED, funnelParams(params));
}

/** Leaving for Stripe Checkout. Not a completed payment. */
export function trackCheckoutStarted(params = {}) {
  sendEvent(FUNNEL_EVENT_NAMES.CHECKOUT_STARTED, funnelParams({ currency: 'USD', ...params }));
  // Existing GA4/pipeline name for a booking handoff (kept for reporting continuity).
  trackDiscoveryCallStarted('/book', 'checkout', {
    booking_type: params.booking_type,
    cta_location: 'meeting_checkout',
  });
}

/**
 * Paid and booked, confirmed by the server. `dedupeKey` (not sent) stops a
 * page reload from counting the same booking twice.
 */
export function trackMeetingBooked(dedupeKey, params = {}) {
  if (!oncePerSession(`booked_${String(dedupeKey || '').slice(-12)}`)) return false;
  sendEvent(FUNNEL_EVENT_NAMES.MEETING_BOOKED, funnelParams({ currency: 'USD', ...params }));
  // Existing GA4 key event and pipeline metric; now fired only for a paid, server-confirmed booking.
  trackDiscoveryCallBooked(params.booking_type, { cta_location: 'meeting_confirmed' });
  return true;
}

/** An agreement was signed. Sent from the token-free confirmation page only. */
export function trackAgreementSigned(params = {}) {
  sendEvent(FUNNEL_EVENT_NAMES.AGREEMENT_SIGNED, funnelParams(params));
}

/** A comprehensive plan or ongoing planning agreement was signed. */
export function trackPlanPurchase(params = {}) {
  sendEvent(FUNNEL_EVENT_NAMES.PLAN_PURCHASE, funnelParams({ currency: 'USD', ...params }));
}

export function getDestinationDomain(url) {
  if (!url) return '';
  if (url.startsWith('mailto:')) return 'email';
  if (url.startsWith('sms:') || url.startsWith('tel:')) return 'phone';

  try {
    return new URL(url, window.location.origin).hostname;
  } catch (_error) {
    return '';
  }
}

/**
 * Loads GA4 unless the visitor declined (or sent GPC). Before a decision it runs
 * cookieless: analytics_storage stays denied, so Google gets ID-free pings and
 * sets no cookies. Allowing analytics upgrades it in place. Safe to call repeatedly.
 */
export function initializeAnalytics() {
  preserveUtmParams();

  if (!GA_MEASUREMENT_ID || typeof window === 'undefined') return;
  // Never load the tag while a signing-link token is in the URL.
  if (isTokenPath(window.location.pathname)) return;
  const mode = analyticsMode();
  if (mode === 'off') return;
  const analyticsStorage = mode === 'granted' ? 'granted' : 'denied';
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;

  if (window.__bpaAnalyticsInitialized) {
    window.gtag('consent', 'update', { analytics_storage: analyticsStorage });
    return;
  }

  window.__bpaAnalyticsInitialized = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  // Consent Mode v2: advertising storage and signals stay denied; analytics
  // cookies only after an explicit opt-in.
  window.gtag('consent', 'default', {
    analytics_storage: analyticsStorage,
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}

const PERSISTENT_ANALYTICS_KEYS = [FIRST_TOUCH_KEY, LATEST_TOUCH_KEY];

function expireGoogleAnalyticsCookies() {
  if (typeof document === 'undefined') return;
  const names = document.cookie
    .split(';')
    .map((part) => part.split('=')[0].trim())
    .filter((name) => name === '_ga' || name.startsWith('_ga_') || name === '_gid');
  const hostParts = window.location.hostname.split('.');
  const domains = [''];
  for (let i = 0; i < hostParts.length - 1; i += 1) {
    domains.push(`; domain=.${hostParts.slice(i).join('.')}`);
  }
  names.forEach((name) => {
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    });
  });
}

/** Stops GA4 and removes analytics cookies and persistent identifiers after a decline. */
export function disableAnalytics() {
  if (typeof window === 'undefined') return;
  if (GA_MEASUREMENT_ID) {
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', { analytics_storage: 'denied' });
    }
  }
  try {
    PERSISTENT_ANALYTICS_KEYS.forEach((key) => window.localStorage.removeItem(key));
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith('bpa_analytics_'))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch (_error) {
    // Storage unavailable — nothing to clear.
  }
  expireGoogleAnalyticsCookies();
}

/** Token-free address shown once an agreement has been signed. */
export const AGREEMENT_SIGNED_PATH = '/agreement-signed';

/**
 * After signing, replace the secret signing link in the address bar with a
 * token-free path, then start analytics. Events sent afterwards report
 * /agreement-signed as the page and can never include the token.
 */
export function leaveTokenUrlForAnalytics() {
  if (typeof window === 'undefined') return;
  if (isTokenPath(window.location.pathname)) {
    window.history.replaceState(window.history.state, '', AGREEMENT_SIGNED_PATH);
  }
  initializeAnalytics();
}

let lastTrackedPagePath = null;

export function trackPageView(path) {
  if (!isPublicMarketingPath(path)) {
    return;
  }

  captureOriginalLandingPage(path, typeof window !== 'undefined' ? window.location.hash : '');
  if (lastTrackedPagePath && lastTrackedPagePath !== path) {
    captureReferringPage(lastTrackedPagePath);
  }
  lastTrackedPagePath = path;

  if (analyticsMode() === 'off') return;
  if (!GA_MEASUREMENT_ID || typeof window.gtag !== 'function') return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: `${window.location.origin}${window.location.pathname}`,
    page_title: document.title,
    ...sanitizeParams(),
  });
}

export function trackEvent(eventName, params = {}) {
  sendEvent(eventName, params);
}
